import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, createTheme, rem } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import './index.css';
import './styles/tabs.css';
import './styles/segmented-control.css'; // MED-161: Toggle button styling
import './styles/mobile.css'; // MED-732: Mobile-responsive view cleanup
import App from './App';

/**
 * Medley Design System Colors
 * See /docs/STYLE_GUIDE.md for complete reference
 */

// Primary Green - Navigation, primary buttons, active states
const medleyGreen = [
  '#f4f7f5', // 0 - lightest bg
  '#e8efe9', // 1 - light bg
  '#c5d6c8', // 2
  '#9fba9f', // 3
  '#7a9e7a', // 4
  '#5a8558', // 5
  '#4A7C59', // 6 - Success Green
  '#3a5f3a', // 7
  '#28463A', // 8 - Primary brand color (header, buttons) - Updated per MED-129
  '#1f3027', // 9 - darkest
] as const;

// Warning Amber - Warnings, current/active items, CTAs
const warningAmber = [
  '#fdf9f0', // 0
  '#FCF0D4', // 1 - Warning Background
  '#f2e0b5', // 2
  '#ebcf8a', // 3
  '#e4be5f', // 4
  '#E9A23B', // 5 - Warning Amber primary
  '#D4922F', // 6 - hover
  '#92650E', // 7 - warning text
  '#8f6d28', // 8
  '#75581f', // 9
] as const;

// Danger Red - Errors, destructive actions, live status
const dangerRed = [
  '#fef2f2', // 0
  '#fee2e2', // 1
  '#fecaca', // 2
  '#fca5a5', // 3
  '#f87171', // 4
  '#D64545', // 5 - Danger Red primary
  '#C13A3A', // 6 - hover
  '#b91c1c', // 7
  '#991b1b', // 8
  '#7f1d1d', // 9
] as const;

// Group Colors
const groupAlpha = [
  '#f7f8f3',
  'rgba(139,155,110,0.15)', // 1 - background
  '#dde1cc',
  '#c8cfae',
  '#b4bd92',
  '#8B9B6E', // 5 - Alpha (Olive Green)
  '#5B7B4E', // 6 - text color
  '#6f7d4e',
  '#566040',
  '#3e4532',
] as const;

const groupBeta = [
  '#f0f4f7',
  'rgba(110,139,158,0.15)', // 1 - background
  '#d1dce4',
  '#b3c5d1',
  '#94aebe',
  '#6E8B9E', // 5 - Beta (Steel Blue)
  '#4E6B7B', // 6 - text color
  '#526d7f',
  '#3d5363',
  '#2a3a47',
] as const;

const groupGamma = [
  '#fdf5ef',
  'rgba(198,122,60,0.15)', // 1 - background
  '#f5dcc8',
  '#edc4a0',
  '#e5ab78',
  '#C67A3C', // 5 - Gamma (Burnt Orange)
  '#9E5A2A', // 6 - text color
  '#8b5a2b',
  '#6f4722',
  '#53341a',
] as const;

// MED-161: Restructured so filled variant (index 8 = primaryShade) uses spec color
const surveyPurple = [
  '#faf8fd', // 0
  'rgba(139,92,246,0.1)', // 1 - background for light variant
  '#e9d5f5', // 2
  '#d8bef0', // 3
  '#c7a7eb', // 4
  '#a78bfa', // 5
  '#9775f5', // 6
  '#8f6af3', // 7
  '#8B5CF6', // 8 - In Design background (spec color) - used by filled variant (primaryShade=8)
  '#6d3dc0', // 9 - darkest (for hover states)
] as const;

// MED-160: Cohort Status Colors
// Custom colors for cohort status pills matching design spec

// MED-161: Draft/Not Started - #E8E6E3 background with #666666 dark text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const statusDraft = [
  '#ffffff', // 0 - lightest
  '#fcfbfa', // 1
  '#f8f7f5', // 2
  '#f5f3f0', // 3
  '#f2f0ed', // 4
  '#efedea', // 5
  '#ecebe7', // 6
  '#e9e8e5', // 7
  '#E8E6E3', // 8 - Draft/Not Started background (spec color) - used by filled variant (primaryShade=8)
  '#c5c3c0', // 9 - darkest (for hover states)
] as const;

// MED-161: In Prep - #C1DBEF background with #28463A dark text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const statusInPrep = [
  '#ffffff', // 0 - lightest
  '#f7fafc', // 1
  '#eef5fa', // 2
  '#e5eff6', // 3
  '#dce9f3', // 4
  '#d3e4ef', // 5
  '#cadeec', // 6
  '#c5dcea', // 7
  '#C1DBEF', // 8 - In Prep background (spec color) - used by filled variant (primaryShade=8)
  '#8fb7d7', // 9 - darkest (for hover states)
] as const;

// MED-161: Live/Medium - #F09A65 background with white text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const statusLive = [
  '#fef7f3', // 0
  '#fdeee6', // 1
  '#fcd9c4', // 2
  '#fac4a2', // 3
  '#f8b080', // 4
  '#f6a876', // 5
  '#f4a06c', // 6
  '#f29c68', // 7
  '#F09A65', // 8 - Live/Medium background (spec color) - used by filled variant (primaryShade=8)
  '#c87a45', // 9 - darkest (for hover states)
] as const;

// MED-161: Completed - #4CAF50 background with white text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const statusCompleted = [
  '#f2f8f4', // 0
  '#e6f1e9', // 1
  '#c2dfcb', // 2
  '#9eccad', // 3
  '#7aba8f', // 4
  '#66b86a', // 5
  '#5cb460', // 6
  '#52b156', // 7
  '#4CAF50', // 8 - Completed background (spec color) - used by filled variant (primaryShade=8)
  '#2e7d32', // 9 - darkest (for hover states)
] as const;

// MED-161: Task In Progress - #D1D69B background with #28463A dark text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const taskInProgress = [
  '#fafbf5', // 0
  '#f5f7eb', // 1
  '#f0f2e0', // 2
  '#ebefd5', // 3
  '#e6eaca', // 4
  '#e1e5bf', // 5
  '#dce0b4', // 6
  '#d7dba9', // 7
  '#D1D69B', // 8 - In Progress background (spec color) - used by filled variant (primaryShade=8)
  '#a8ad7b', // 9 - darkest (for hover states)
] as const;

// MED-161: Importance High - #E53935 background with white text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const importanceHigh = [
  '#fef2f2', // 0
  '#fee2e2', // 1
  '#fecaca', // 2
  '#fca5a5', // 3
  '#f87171', // 4
  '#f35551', // 5
  '#ee4743', // 6
  '#e9403c', // 7
  '#E53935', // 8 - High importance background (spec color) - used by filled variant (primaryShade=8)
  '#b71c1c', // 9 - darkest (for hover states)
] as const;

// MED-161: Cancelled - #888888 background with white text
// NOTE: Index [8] is used for 'filled' variant (primaryShade=8), so spec color goes there
const statusCancelled = [
  '#fafafa', // 0
  '#f5f5f5', // 1
  '#e5e5e5', // 2
  '#d4d4d4', // 3
  '#b8b8b8', // 4
  '#a8a8a8', // 5
  '#989898', // 6
  '#909090', // 7
  '#888888', // 8 - Cancelled background (spec color) - used by filled variant (primaryShade=8)
  '#525252', // 9 - darkest (for hover states)
] as const;

// MED-182: Gold/Tan color for user avatars in header
// Primary shade is #C4A77D (tan/gold) - provides good contrast with white text
const medleyGold = [
  '#fdfcfa', // 0 - lightest
  '#faf6f0', // 1
  '#f5ede0', // 2
  '#f0e4d0', // 3
  '#e5d4b8', // 4
  '#d9c4a0', // 5
  '#C4A77D', // 6 - primary gold (used by Avatar)
  '#a8905f', // 7 - darker
  '#8c7645', // 8
  '#705d30', // 9 - darkest
] as const;

// MED-136: Custom gray palette with updated gray-0 (#F9F7F4)
const gray = [
  '#F9F7F4', // 0 - MED-136: updated from Mantine default
  '#f1f3f5', // 1
  '#e9ecef', // 2
  '#dee2e6', // 3
  '#ced4da', // 4
  '#adb5bd', // 5
  '#868e96', // 6
  '#495057', // 7
  '#343a40', // 8
  '#212529', // 9
] as const;

const theme = createTheme({
  primaryColor: 'medleyGreen',
  primaryShade: 8, // Use index 8 (#2D4739 Primary Green) instead of default 6 (#4A7C59 Success Green)
  colors: {
    medleyGreen,
    medleyGold, // MED-182: Gold/tan for user avatars
    warningAmber,
    dangerRed,
    groupAlpha,
    groupBeta,
    groupGamma,
    surveyPurple,
    gray, // MED-136: custom gray with updated gray-0
    statusDraft, // MED-160: Cohort status colors
    statusInPrep, // MED-160: Cohort status colors
    statusLive, // MED-160/MED-161: Cohort status colors
    statusCompleted, // MED-160/MED-161: Cohort status colors
    taskInProgress, // MED-161: Task status colors
    importanceHigh, // MED-161: Task importance colors
    statusCancelled, // MED-161: Cohort/status cancelled color
  },
  // Style Guide: Overpass is the primary typeface. MED-1350: Medley Middle Dot
  // (src/index.css) only gives the middle dot a width.
  fontFamily:
    'Medley Middle Dot, Overpass, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  headings: {
    fontFamily:
      'Medley Middle Dot, Overpass, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    fontWeight: '700', // Style Guide: headings use 700 weight
  },
  black: '#1A1A1A',
  defaultRadius: 'md',
  // Style Guide: 4px grid spacing system
  spacing: {
    xs: rem(8), // space-4
    sm: rem(12), // space-6
    md: rem(16), // space-8
    lg: rem(20), // space-10
    xl: rem(24), // space-12
  },
  components: {
    Checkbox: {
      defaultProps: {
        radius: 4,
      },
    },
    // MED-228: Container width xl (100rem/1600px) - increased from 82.5rem for more horizontal space
    Container: {
      defaultProps: {
        size: 'xl',
      },
      styles: {
        root: {
          maxWidth: rem(1600), // 100rem = 1600px
        },
      },
    },
    Button: {
      defaultProps: {
        radius: 8, // MED-129: 8px radius for primary/secondary
      },
      styles: {
        root: {
          fontWeight: 500, // MED-129: 500 weight
        },
      },
      vars: (_theme: unknown, props: { variant?: string }) => {
        // MED-129: Tertiary/outline buttons have 6px radius
        if (props.variant === 'outline') {
          return {
            root: {
              '--button-radius': '6px',
            },
          };
        }
        return { root: {} };
      },
    },
    Card: {
      defaultProps: {
        radius: 12, // MED-129: 12px radius
        withBorder: false, // MED-129: no borders
      },
      styles: {
        root: {
          // boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.10)', // MED-129: new shadow
          boxShadow: 'none',
        },
      },
    },
    Paper: {
      defaultProps: {
        radius: 12, // MED-129: 12px radius
      },
      styles: {
        root: {
          // boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.10)', // MED-129: new shadow
          boxShadow: 'none',
        },
      },
    },
    Pagination: {
      styles: {
        control: {
          minWidth: 32,
          height: 32,
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
        },
      },
    },
    TextInput: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          height: '36px',
          minHeight: '36px',
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingRight: '12px',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          height: '36px',
          minHeight: '36px',
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingRight: '12px',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    Select: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          height: '36px',
          minHeight: '36px',
          padding: '8px 12px',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    MultiSelect: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          minHeight: '36px',
          padding: '4px 12px',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    Textarea: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          padding: '8px 12px',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    NumberInput: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          height: '36px',
          minHeight: '36px',
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingInlineStart: '12px',
          // MED-676: Reserve space for the stepper controls PLUS an 8px
          // visual buffer so text doesn't sit flush against the chevrons.
          // `--input-right-section-size` scales with the input `size` prop
          // (17px xs / 24px sm / 27px md / 31px lg / 34px xl), so calc()
          // keeps the buffer proportional across all sizes. Falls back to
          // 27px (md default) when the variable is absent (e.g. no right section).
          paddingInlineEnd: 'calc(var(--input-right-section-size, 1.6875rem) + 0.5rem)',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    DatePickerInput: {
      defaultProps: {
        radius: 6,
      },
      styles: {
        input: {
          height: '36px',
          minHeight: '36px',
          padding: '8px 12px',
          borderColor: '#E0DDD8',
          '&:disabled': {
            backgroundColor: '#F9F8F6',
            borderColor: '#E0DDD8',
            color: '#888888',
            opacity: 1,
          },
        },
      },
    },
    Badge: {
      defaultProps: {
        radius: 'sm', // Style Guide: radius-sm = 3px for status pills
      },
      styles: {
        root: {
          fontWeight: 600, // Style Guide: 600 weight
          textTransform: 'uppercase',
          fontSize: rem(10),
          letterSpacing: '0.3px', // Style Guide: 0.3px letter-spacing
        },
      },
      // MED-161: Force correct text colors for light background badges
      vars: (_theme: unknown, props: { color?: string; variant?: string }) => {
        // Only override for filled variant
        if (props.variant !== 'filled') {
          return { root: {} };
        }
        // Light backgrounds need dark text
        if (props.color === 'statusDraft') {
          return { root: { '--badge-color': '#666666' } };
        }
        if (props.color === 'statusInPrep') {
          return { root: { '--badge-color': '#28463A' } };
        }
        if (props.color === 'taskInProgress') {
          return { root: { '--badge-color': '#28463A' } };
        }
        return { root: {} };
      },
    },
    Tabs: {
      styles: {
        tab: {
          fontWeight: 500,
        },
      },
    },
    Table: {
      styles: {
        th: {
          fontWeight: 600,
          fontSize: rem(10), // Style Guide: 10px for table headers
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          color: '#888888', // Style Guide: Text Tertiary
          backgroundColor: '#FAFAF8', // Style Guide: Surface Light
        },
        td: {
          borderBottomColor: '#F0EEEA', // Style Guide: table row border
        },
      },
    },
    Modal: {
      defaultProps: {
        radius: 'lg', // Style Guide: radius-xl = 8px for modals
      },
      styles: {
        content: {
          // MED-265: updated from 800px.
          // MDF-233: a fixed minWidth made every modal wider than a narrow
          // viewport, and centring then pushed its left edge off screen with
          // no horizontal scroll to reach it. min-width resolves after
          // max-width in CSS, so the clamp has to live in this value.
          // Identical to 900px at any viewport of 932px or wider.
          minWidth: 'min(900px, calc(100vw - 32px))',
        },
        header: {
          padding: '24px 32px 0 32px', // MED-136: modal padding
        },
        title: {
          color: '#28463A', // MED-138: modal title color
          fontWeight: 700, // MED-138: modal title weight
          fontSize: '20px', // MED-138: modal title size
        },
        body: {
          padding: '0 32px 24px', // MED-265: removed excess top padding (header provides top spacing)
        },
        close: {
          backgroundColor: '#F5F3F0', // MED-136: close button background
          borderRadius: '10px', // MED-136: close button radius
          width: '32px',
          height: '32px',
          '&:hover': {
            backgroundColor: '#ECEAE6',
          },
        },
      },
    },
    Menu: {
      defaultProps: {
        radius: 'md',
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'md',
      },
    },
    Avatar: {
      defaultProps: {
        radius: 'xl', // Style Guide: radius-full = 50% for avatars
      },
    },
    Progress: {
      defaultProps: {
        radius: 'xs', // Style Guide: radius-xs = 2px for progress bars
        size: 'sm',
      },
    },
    // MED-138: Alert/Callout styling
    Alert: {
      defaultProps: {
        radius: 'md',
      },
      vars: (_theme: unknown, props: { color?: string; variant?: string }) => {
        // Default/gray callout: Background #F9F7F4, Text color #666666
        if (props.color === 'gray' && props.variant === 'light') {
          return {
            root: {
              '--alert-bg': '#F9F7F4',
              '--alert-color': '#666666',
            },
          };
        }
        // Info/blue callout: Background rgba(59, 130, 246, 0.08)
        if (props.color === 'blue' && props.variant === 'light') {
          return {
            root: {
              '--alert-bg': 'rgba(59, 130, 246, 0.08)',
              '--alert-bd': '#3B82F6',
            },
          };
        }
        return { root: {} };
      },
    },
    // MED-161: Toggle button (SegmentedControl) styling
    // Note: Active state selectors are in /src/styles/segmented-control.css
    SegmentedControl: {
      styles: {
        root: {
          backgroundColor: '#FFFFFF',
          padding: '4px',
          borderRadius: '10px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.08)',
        },
        indicator: {
          backgroundColor: '#28463A', // Primary green for active state
          borderRadius: '8px',
          boxShadow: 'none',
        },
        label: {
          fontSize: rem(12),
          fontWeight: 500,
          padding: '8px 16px',
          color: '#888888', // Inactive text color (active state in CSS)
        },
        control: {
          borderRadius: '8px',
          border: 'none',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <DatesProvider settings={{ firstDayOfWeek: 0, consistentWeeks: true }}>
          <ModalsProvider>
            <Notifications position="top-right" />
            <App />
          </ModalsProvider>
        </DatesProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>
);
